import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, X, Shield, User, UserCheck, UserX, RefreshCw, Mail, Trash2, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import Modal from './Modal';
import { notifyToast } from '../../lib/toastStore';

interface OrgAgent {
  uid: string;
  email: string;
  agentRole: 'super' | 'regular';
  status: string;
  invitedBy?: string;
  invitedByEmail?: string;
  invitedAt?: string;
  acceptedAt?: string;
  /** The org's designated Super Agent (owner) — protected row: no
   *  remove/demote/deactivate affordances for anyone (server enforces). */
  isOwner?: boolean;
  /** Who deactivated this member ('super' | 'admin'); admin deactivations
   *  can only be lifted by an admin. */
  deactivatedBy?: string;
  /** Uid of the super/admin who deactivated this member (provenance). */
  deactivatedByUid?: string;
}

/**
 * TEAMS & ROLES: this component manages an ORGANIZATION TEAM only (the
 * Referrer Team, Employer Team, or Sponsor Team selected by apiPrefix) —
 * never the workspace team, which lives in Workspace Settings. See the
 * canonical note above isOrgConsoleProfile in serverWorkspaces.ts.
 */
interface OrgTeamManagementProps {
  apiPrefix: string; // '/api/employer', '/api/sponsor', '/api/referrer'
  title?: string;
  currentUserId?: string; // If provided, prevents the user from removing themselves
}

export default function OrgTeamManagement({ apiPrefix, title = 'Organization Team', currentUserId }: OrgTeamManagementProps) {
  const [agents, setAgents] = useState<OrgAgent[]>([]);
  const [isSuperAgent, setIsSuperAgent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const closeInviteModal = () => { setShowInviteModal(false); setInviteEmail(''); };
  // Promote/demote run through the org's DIRECT endpoints (the old
  // password+OTP DoubleConfirmationModal flow requires workspace
  // settings:edit, which org accounts never hold — it 403'd for every org
  // super). A plain in-app confirmation is used instead; the server's
  // identity-based Super Agent protection is the authority.
  const [roleChangeTarget, setRoleChangeTarget] = useState<{ uid: string; email: string; newRole: 'super' | 'regular' } | null>(null);
  const [changingRole, setChangingRole] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<{ uid: string; email: string } | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  // FIX 2026-10-07: in-app confirmation modal for remove (replaces native confirm())
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ uid: string; email: string; status?: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  // REF-16: the invite dialog and the three confirmation dialogs
  // below render through the shared Modal, which registers them with
  // the modal stack itself (they previously hand-rolled their layers:
  // SYS-11 had already replaced an invented z-index 100 with stack
  // registration via useModalLayer).

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  const [accessDenied, setAccessDenied] = useState(false);
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const data = await safeFetch(`${apiPrefix}/agents`);
      setAgents(data.agents || []);
      setIsSuperAgent(data.isSuperAgent || false);
    } catch (err: any) {
      // MJ-1: regular members are 403'd on the roster by design — show the
      // restricted notice instead of an error toast.
      if (/403|Super Agent access required/i.test(err?.message || '')) {
        setAccessDenied(true);
      } else {
        showToast(err.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleInvite = async () => {
    if (inviting || !inviteEmail.trim()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    setInviting(true);
    try {
      await safeFetch(`${apiPrefix}/agents/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase() }),
      });
      showToast('Invitation sent — they\'ll get an email with a sign-in link', 'success');
      setInviteEmail('');
      setShowInviteModal(false);
      fetchAgents();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setInviting(false);
    }
  };

  const handlePromote = (agent: OrgAgent) => {
    setRoleChangeTarget({ uid: agent.uid, email: agent.email, newRole: 'super' });
  };

  const handleDemote = (agent: OrgAgent) => {
    setRoleChangeTarget({ uid: agent.uid, email: agent.email, newRole: 'regular' });
  };

  const confirmRoleChange = async () => {
    if (!roleChangeTarget) return;
    setChangingRole(true);
    try {
      const action = roleChangeTarget.newRole === 'super' ? 'promote' : 'demote';
      await safeFetch(`${apiPrefix}/agents/${roleChangeTarget.uid}/${action}`, { method: 'POST' });
      showToast(roleChangeTarget.newRole === 'super' ? 'Promoted to Super Agent' : 'Demoted to Regular Agent', 'success');
      fetchAgents();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setChangingRole(false);
      setRoleChangeTarget(null);
    }
  };

  const handleDeactivate = (agent: OrgAgent) => {
    setDeactivateTarget({ uid: agent.uid, email: agent.email });
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await safeFetch(`${apiPrefix}/agents/${deactivateTarget.uid}/deactivate`, { method: 'POST' });
      showToast('Team member deactivated', 'success');
      fetchAgents();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setDeactivating(false);
      setDeactivateTarget(null);
    }
  };

  const handleRemove = async (uid: string, email: string, status?: string) => {
    // FIX 2026-10-07: use in-app modal instead of native confirm() which
    // never appears in some webview/embedded environments.
    setRemoveTarget({ uid, email, status });
    setRemoveConfirmOpen(true);
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await safeFetch(`${apiPrefix}/agents/${removeTarget.uid}/remove`, { method: 'POST' });
      showToast('Agent removed', 'success');
      fetchAgents();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setRemoving(false);
      setRemoveConfirmOpen(false);
      setRemoveTarget(null);
    }
  };

  const handleReactivate = async (uid: string) => {
    try {
      await safeFetch(`${apiPrefix}/agents/${uid}/reactivate`, { method: 'POST' });
      showToast('Agent reactivated', 'success');
      fetchAgents();
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  };

  const superAgentCount = agents.filter(a => a.agentRole === 'super').length;

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Toasts render via the global root-level notification layer. */}

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900">{title}</h2>
              <p className="text-[11px] text-slate-500">
                {loading ? (
                  <span className="inline-block h-2 w-24 rounded bg-slate-100 animate-pulse align-middle" aria-label="Loading team members" />
                ) : (
                  <>{agents.length} agent{agents.length !== 1 ? 's' : ''} · {superAgentCount} super agent{superAgentCount !== 1 ? 's' : ''}</>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAgent && (
              <button onClick={() => setShowInviteModal(true)}
                className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition cursor-pointer flex items-center gap-1.5 shadow-lg shadow-blue-600/20">
                <Plus className="h-3.5 w-3.5" /> Invite Agent
              </button>
            )}
            <button onClick={fetchAgents} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition cursor-pointer">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {isSuperAgent && (
          <div className="bg-blue-50 rounded-xl px-4 py-3 mb-6">
            <p className="text-xs text-blue-700 font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              You are a Super Agent — you can invite, promote, demote, deactivate, and remove team members.
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="premium-loader" /></div>
        ) : accessDenied ? (
          <div className="text-center py-16">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-600 mb-1">Restricted area</p>
            <p className="text-xs text-slate-500">Only Super Agents can view and manage team members.</p>
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-600 mb-1">No team members yet</p>
            <p className="text-xs text-slate-500 mb-4">Invite agents to collaborate on this organization.</p>
            {isSuperAgent && (
              <button onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl cursor-pointer shadow-lg shadow-blue-600/20">
                <Plus className="h-3.5 w-3.5 inline mr-1" /> Invite Agent
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {agents.map((agent) => (
              <div key={agent.uid} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    agent.agentRole === 'super' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {agent.agentRole === 'super' ? <Shield className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs font-bold text-slate-900 break-all">{agent.email}</p>
                      {agent.agentRole === 'super' ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700">Super Agent</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-500">Regular Agent</span>
                      )}
                      {agent.isOwner && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700" title="The organization's designated Super Agent — this role can only be changed by an administrator">Team Owner</span>
                      )}
                      {agent.status === 'active' ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700">Active</span>
                      ) : agent.status === 'pending' ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700" title="Invited — hasn't signed in yet">Pending</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700 capitalize">{agent.status}</span>
                      )}
                    </div>
                    {agent.invitedAt && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Invited {new Date(agent.invitedAt).toLocaleDateString()}
                        {agent.invitedByEmail ? ` by ${agent.invitedByEmail}` : ''}
                        {agent.status === 'pending' && ' — invite email sent'}
                      </p>
                    )}
                    {agent.status === 'blocked' && agent.deactivatedBy === 'admin' && (
                      <p className="text-[10px] text-red-400 mt-0.5">Deactivated by admin — only an administrator can reactivate this member.</p>
                    )}
                    {agent.status === 'blocked' && agent.deactivatedBy === 'super' && agent.deactivatedByUid && agent.deactivatedByUid === currentUserId && (
                      <p className="text-[11px] text-slate-500 mt-0.5">Deactivated by you — you can reactivate them anytime.</p>
                    )}
                    {agent.status === 'blocked' && agent.deactivatedBy === 'super' && (!agent.deactivatedByUid || agent.deactivatedByUid !== currentUserId) && (
                      <p className="text-[11px] text-slate-500 mt-0.5">Deactivated by a Super Agent — a Super Agent can reactivate them.</p>
                    )}
                  </div>
                </div>
                {isSuperAgent && !agent.isOwner && (
                  <div className="flex items-center gap-1 shrink-0">
                    {agent.agentRole === 'regular' ? (
                      <button onClick={() => handlePromote(agent)}
                        className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer"
                        title="Promote to Super Agent"
                        aria-label={`Promote ${agent.email} to Super Agent`}>
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button onClick={() => superAgentCount > 1 && handleDemote(agent)}
                        disabled={superAgentCount <= 1}
                        className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        title={superAgentCount > 1 ? 'Demote to Regular Agent' : 'Cannot demote the last Super Agent — promote someone else first'}
                        aria-label={`Demote ${agent.email} to Regular Agent`}>
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {agent.status !== 'active' ? (
                      <button onClick={() => handleReactivate(agent.uid)}
                        className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-emerald-50 text-emerald-600 transition cursor-pointer"
                        title={agent.status === 'pending' ? 'Resend Invite' : 'Reactivate'}
                        aria-label={agent.status === 'pending' ? `Resend invitation to ${agent.email}` : `Reactivate ${agent.email}`}>
                        <UserCheck className="h-3.5 w-3.5" />
                      </button>
                    ) : agent.uid !== currentUserId ? (
                      <button onClick={() => handleDeactivate(agent)}
                        className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-orange-50 text-orange-500 transition cursor-pointer"
                        title="Deactivate — ends their team access until reactivated"
                        aria-label={`Deactivate ${agent.email}`}>
                        <UserX className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                    {agent.uid !== currentUserId && (
                      <button onClick={() => handleRemove(agent.uid, agent.email, agent.status)}
                        className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 transition cursor-pointer"
                        title={agent.status === 'pending' ? 'Cancel invitation' : 'Remove from organization'}
                        aria-label={agent.status === 'pending' ? `Cancel invitation for ${agent.email}` : `Remove ${agent.email} from organization`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                )}
                {isSuperAgent && agent.isOwner && (
                  <span className="text-[11px] text-slate-500 shrink-0" title="The designated Super Agent cannot be removed, demoted, or deactivated — an administrator must change the Super Agent first">Protected</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Agent Modal — REF-16: shared Modal (portal, stack
          layer, scroll lock, Escape) instead of the manual portal. */}
      {showInviteModal && (
        <Modal onClose={closeInviteModal} ariaLabel="Invite Agent">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">Invite Agent</h3>
              <button onClick={closeInviteModal} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-slate-100 transition cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="org-team-invite-email" className="block text-xs font-bold text-slate-700 mb-1">Email Address</label>
                <input id="org-team-invite-email" type="email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="w-full px-3 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  onKeyDown={e => e.key === 'Enter' && handleInvite()} />
              </div>
              <div className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-xs text-slate-500 font-medium flex items-start gap-2">
                  <Mail className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <span>They'll get an email with a secure sign-in link to create their password and open the dashboard. They can also sign in with an email verification code. A Super Agent can promote them later.</span>
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={closeInviteModal}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">
                  Cancel
                </button>
                <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition cursor-pointer flex items-center gap-1.5">
                  {inviting ? 'Inviting...' : 'Invite Agent'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {/* Promote/demote confirmation — calls the org's direct endpoints */}
      {roleChangeTarget && (
        <Modal size="sm" onClose={() => { if (!changingRole) setRoleChangeTarget(null); }} ariaLabel={roleChangeTarget.newRole === 'super' ? 'Promote to Super Agent?' : 'Demote to Regular Agent?'}>
          <div className="bg-white rounded-2xl shadow-xl w-full p-6">
            <h3 className="text-base font-bold text-slate-800">
              {roleChangeTarget.newRole === 'super' ? 'Promote to Super Agent?' : 'Demote to Regular Agent?'}
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              {roleChangeTarget.newRole === 'super' ? (
                <><span className="font-semibold">{roleChangeTarget.email}</span> will be able to invite, manage, and remove team members.</>
              ) : (
                <><span className="font-semibold">{roleChangeTarget.email}</span> will no longer be able to manage this team.</>
              )}
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setRoleChangeTarget(null)}
                disabled={changingRole}
                className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRoleChange}
                disabled={changingRole}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {changingRole ? 'Saving...' : roleChangeTarget.newRole === 'super' ? 'Promote' : 'Demote'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Deactivate confirmation */}
      {deactivateTarget && (
        <Modal size="sm" onClose={() => { if (!deactivating) setDeactivateTarget(null); }} ariaLabel="Deactivate team member?">
          <div className="bg-white rounded-2xl shadow-xl w-full p-6">
            <h3 className="text-base font-bold text-slate-800">Deactivate team member?</h3>
            <p className="text-sm text-slate-600 mt-2">
              Deactivate <span className="font-semibold">{deactivateTarget.email}</span>? They will lose access to this organization's team immediately. Their own account is not affected, and you can reactivate them later.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setDeactivateTarget(null)}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition disabled:opacity-50"
              >
                {deactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* FIX 2026-10-07: in-app remove confirmation (replaces window.confirm) */}
      {removeConfirmOpen && removeTarget && (
        <Modal size="sm" onClose={() => { if (!removing) setRemoveConfirmOpen(false); }} ariaLabel={removeTarget.status === 'pending' ? 'Cancel invitation?' : 'Remove team member?'}>
          <div className="bg-white rounded-2xl shadow-xl w-full p-6">
            <h3 className="text-base font-bold text-slate-800">{removeTarget.status === 'pending' ? 'Cancel invitation?' : 'Remove team member?'}</h3>
            <p className="text-sm text-slate-600 mt-2">
              {removeTarget.status === 'pending' ? (
                <>Cancel the invitation for <span className="font-semibold">{removeTarget.email}</span>? The invitation link they were emailed will stop working.</>
              ) : (
                <>Remove <span className="font-semibold">{removeTarget.email}</span> from this organization? They will lose access immediately.</>
              )}
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setRemoveConfirmOpen(false)}
                disabled={removing}
                className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                disabled={removing}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {removing ? 'Working...' : removeTarget.status === 'pending' ? 'Cancel invitation' : 'Remove'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
